import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { changePassword } from '../../Action/api';

export const ResetPasswordDialog = ({ open, onClose, onSuccess, userData: initialUserData }) => {
  console.log('ResetPasswordDialog rendered with props:', { open, initialUserData });
  
    const [localUserData, setLocalUserData] = useState(null);

  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update the useEffect for initialUserData
  useEffect(() => {
    console.log('Dialog opened with userData:', initialUserData); // Add debug log
    if (initialUserData) {
      setLocalUserData(initialUserData);
    }
  }, [initialUserData]);

  useEffect(() => {
    if (!open) {
      setNewPassword('');
      setError('');
      setIsSubmitting(false);
    }
  }, [open]);

  // Update password validation
  const handleSubmit = async () => {
    console.log('Submit clicked with localUserData:', localUserData);
    console.log('Original userData:', initialUserData);

    // Validate user data first
    if (!localUserData?.userId) {
      console.error('Missing user data:', { localUserData, initialUserData });
      setError('Cannot reset password: User data is missing');
      return;
    }

    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }

    console.log('Attempting to reset password for:', localUserData?.userId);
    setIsSubmitting(true);
    setError('');

    try {
      const requestPayload = {
        userId: localUserData.userId,
        newPassword: newPassword
      };

      console.log('Sending reset password request:', requestPayload);

      const response = await changePassword(requestPayload);
      console.log('Reset password response:', response);

      if (response && response.status === true) {
        console.log('Password reset successful');
        onSuccess(localUserData?.staffFullName);
        onClose();
        setNewPassword('');
      } else {
        console.error('Password reset failed:', response?.message);
        setError(response?.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error?.message || 'An error occurred while resetting the password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
        }
      }}
    >
      <Box sx={{ 
        background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
        pt: 3, 
        pb: 4,
        px: 3,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 20% 120%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%)',
        }} />
        <Typography variant="h5" sx={{ 
          color: 'white',
          fontWeight: 700,
          position: 'relative',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <LockResetIcon />
          Reset Password
        </Typography>
        <Typography variant="body2" sx={{ 
          color: alpha('#fff', 0.7),
          position: 'relative'
        }}>
          Reset password for {localUserData?.staffFullName}
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3, mt: 1 }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 2 }}
          >
            {error}
          </Alert>
        )}
        
        <TextField
          fullWidth
          label="New Password"
          type={showPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#f8fafc',
            }
          }}
        />
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Password must contain at least 8 characters, one uppercase letter, one lowercase letter, 
          one number and one special character.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button 
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ 
            color: '#64748b',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{
            bgcolor: '#6366f1',
            '&:hover': { bgcolor: '#4f46e5' },
            minWidth: 120,
          }}
        >
          {isSubmitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              <span>Resetting...</span>
            </Box>
          ) : (
            'Reset Password'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetPasswordDialog;